import React from 'react';

import { Box, Flex, Text } from '@chakra-ui/react';
import RecommendationBadge from 'src/components/RecommendationBadge';
import ReviewTableScore from 'src/components/ReviewTableScore';
import { IFetchOrganizationAndJournalsInfo } from 'src/api/queries';
import BetterSuitedForAnotherJournal from './BetterSuitedForAnotherJournal';
import { faFile } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  IPeerReviewFirstAssessment,
  IPeerReviewRevisionAssessment
} from '@publishing-engine/domain/models';
import FileViewerButtonText from '../FileViewerButtonText';
import { DisplayField } from 'src/components/shared/DisplayField';

interface Props {
  reviewAssessment: IPeerReviewRevisionAssessment | IPeerReviewFirstAssessment;
  reviewFieldConfig: any;
  organizationData: IFetchOrganizationAndJournalsInfo;
  submissionId: string;
}

const EditorReview: React.FC<Props> = ({
  reviewAssessment,
  reviewFieldConfig,
  organizationData,
  submissionId
}) => {

  return (
    <Flex
      gap={8}
      background="whiteSecondaryFill"
      flexDirection="column"
      marginY={4}
    >
      {reviewAssessment?.recommendation && (
        <Box flexDirection="column">
          <Text marginBottom={2} textStyle="headingItem">
            Recommendation:
          </Text>
          <RecommendationBadge
            recommendation={reviewAssessment?.recommendation || ''}
          />
        </Box>
      )}
      {reviewAssessment?.feedbackForEditors && (
        <Box flexDirection="column">
          <Text marginBottom={2} textStyle="headingItem">
            Feedback for Editor:
          </Text>
          <Text textStyle="textBody">
            {reviewAssessment?.feedbackForEditors}
          </Text>
        </Box>
      )}

      {((reviewAssessment && reviewAssessment.reviewerAttachmentsEditor) || [])
        .length > 0 ? (
        <Flex flexDirection="column" gap={4}>
          <Text textStyle="headingItem">
            Reviewer Attachments (Editor-only)
          </Text>
          <Flex flexDirection="column" gap={4}>
            {reviewAssessment.reviewerAttachmentsEditor?.map(attachment => (
              <React.Fragment key={attachment.id}>
                <Flex gap={2} alignItems="center">
                  <FontAwesomeIcon icon={faFile} />
                  <Text
                    textStyle="textBody"
                    wordBreak="break-word"
                    whiteSpace="normal"
                    flex={1}
                    minWidth={0}
                  >
                    {attachment.name}
                  </Text>
                  <FileViewerButtonText
                    file={attachment}
                    submissionContextId={submissionId}
                    textStyle="textBody"
                    flexShrink={0}
                  >
                    View
                  </FileViewerButtonText>
                </Flex>
              </React.Fragment>
            ))}
          </Flex>
        </Flex>
      ) : null}

      {reviewAssessment &&
        'unaddressedReviewComments' in reviewAssessment &&
        reviewAssessment.unaddressedReviewComments &&
        reviewFieldConfig?.unaddressedReviewComments && (
          <DisplayField
            fieldName="unaddressedReviewComments"
            value={reviewAssessment.unaddressedReviewComments}
            config={reviewFieldConfig}
            showAdditionalDetails={true}
          />
        )}
      {reviewAssessment?.submissionScores && (
        <Box flexDirection="column">
          <Text textStyle="headingItem">Submission Scores</Text>
          <Box marginX={2} marginTop={4}>
            <ReviewTableScore
              submissionScores={reviewAssessment.submissionScores}
              reviewFieldConfig={reviewFieldConfig}
            />
          </Box>
        </Box>
      )}

      {reviewFieldConfig?.missingReference &&
        reviewAssessment?.missingReference && (
          <DisplayField
            fieldName="missingReference"
            value={reviewAssessment.missingReference}
            config={reviewFieldConfig}
          />
        )}
      {reviewAssessment?.articleWellWritten && (
        <DisplayField
          fieldName="articleWellWritten"
          value={reviewAssessment.articleWellWritten}
          config={reviewFieldConfig}
        />
      )}
      {reviewAssessment &&
        'areaOfExpertise' in reviewAssessment &&
        reviewAssessment?.areaOfExpertise && (
          <DisplayField
            fieldName="areaOfExpertise"
            value={reviewAssessment.areaOfExpertise}
            config={reviewFieldConfig}
          />
        )}
      {reviewFieldConfig?.betterSuitedForAnotherJournal && (
        <BetterSuitedForAnotherJournal
          config={reviewFieldConfig?.betterSuitedForAnotherJournal}
          betterSuitedForAnotherJournal={
            reviewAssessment?.betterSuitedForAnotherJournal || null
          }
          organizationData={organizationData}
        />
      )}
      {reviewAssessment?.conflictOfInterest && (
        <DisplayField
          fieldName="conflictOfInterest"
          value={reviewAssessment.conflictOfInterest}
          config={reviewFieldConfig}
        />
      )}
      {reviewAssessment?.canArticleBeShortened && (
        <DisplayField
          fieldName="canArticleBeShortened"
          value={reviewAssessment.canArticleBeShortened}
          config={reviewFieldConfig}
        />
      )}
      {reviewAssessment?.meritFurtherCoverageUponPublication && (
        <DisplayField
          fieldName="meritFurtherCoverageUponPublication"
          value={reviewAssessment.meritFurtherCoverageUponPublication}
          config={reviewFieldConfig}
        />
      )}
       {reviewAssessment?.consentToReInvite && (
        <DisplayField
          fieldName="consentToReInvite"
          value={reviewAssessment.consentToReInvite}
          config={reviewFieldConfig}
        />
      )}
    </Flex>
  );
};

export default EditorReview;
